/**
 * Ranked, typo-tolerant search over ecolabel device NAMES.
 *
 * The EKOenergy secretariat searches by whatever fragment of a name they have at hand —
 * often a partial name, a differently-worded registry name, or a name with a few letters
 * wrong. Plain subsequence matching (fuzzysort) either missed those or buried the right
 * device in noise, so this ranks instead of just filtering:
 *
 * 1. Exact (accent- and case-insensitive) substring matches always outrank fuzzy ones,
 *    with bonuses for matching at the start of the name or a word.
 * 2. Otherwise the query is matched token-by-token: prefixes, contained tokens, and
 *    Levenshtein-close tokens (typo tolerance) all count, and devices matching more of
 *    the query rank higher. A device only needs one strong token match to appear, so
 *    searching a long compound name still finds a device registered under one word of it.
 *    Tokens are weighted by how rare they are in the catalog: "Tullabeg" (one device)
 *    says far more than "solar" or "farm" (a thousand devices each), so "Tullabeg Solar
 *    Farm" ranks "Tullabeg Solar PV" above every generic "X Solar Farm" instead of tying
 *    with them (EKOenergy feedback). A device is listed when it has one strong token hit
 *    AND its weighted score clears a floor — so a name matching only the generic words of
 *    a query that also has a distinctive word is left out, not buried on row 2,000.
 *
 * The device array is treated as an immutable snapshot: the view replaces it on every
 * (re)load, and the caches below key on the array and the device objects.
 * Other fields (registration ID, address, coordinates) have their own plain column
 * filters — this module deliberately searches the name only.
 */

const SUBSTRING_BASE_SCORE = 2;
const EXACT_FIELD_SCORE = 3;
const MIN_TOKEN_SIMILARITY = 0.5;
const MIN_BEST_TOKEN_SIMILARITY = 0.55;
const MIN_FUZZY_SCORE = 0.45;
// The name column's length: any full device name is searchable as typed, while anything
// longer (pasted text, a crafted deep link) is cut so the per-name token work stays bounded.
// Applied to the NORMALIZED query, so a run of punctuation cannot swallow the real words.
export const MAX_QUERY_LENGTH = 256;

export interface DeviceSearchText {
  name: string;
}

/** Inverse-document-frequency weight of a token: how much of the catalog it rules out. */
type TokenWeight = (token: string) => number;

// PERF: normalization (NFD + Unicode regexes) is the expensive part of a search pass, and
// the device objects are stable between keystrokes — cache the normalized name so each
// pass only normalizes the query. Token weights are likewise cached per device array.
const normalizedNameCache = new WeakMap<DeviceSearchText, string>();
const tokenWeightCache = new WeakMap<
  DeviceSearchText[],
  { total: number; weightOf: TokenWeight }
>();

const normalizedNameOf = (device: DeviceSearchText): string => {
  const cached = normalizedNameCache.get(device);
  if (cached !== undefined) return cached;
  const computed = normalize(device.name);
  normalizedNameCache.set(device, computed);
  return computed;
};

/** Cut a query to MAX_QUERY_LENGTH code points — never mid-character, so a name ending in
 * an emoji or a CJK ideograph outside the BMP cannot leave a lone surrogate behind.
 * Stops at the cap rather than materializing the whole input: a crafted deep link can be
 * as long as the browser allows a URL to be. */
export const clampQuery = (text: string): string => {
  let kept = "";
  let count = 0;
  for (const character of text) {
    if (count === MAX_QUERY_LENGTH) break;
    kept += character;
    count += 1;
  }
  return kept;
};

/** Filter + rank `devices` for a name query; best matches first. Empty query returns the input. */
export const searchEcolabelDevices = <T extends DeviceSearchText>(
  devices: T[],
  query: string
): T[] => {
  const queryNorm = clampQuery(normalize(query)).trim();
  if (!queryNorm) return devices;
  const queryTokens = queryNorm.split(" ");
  const weightOf = tokenWeightsOf(devices);

  const scored: { item: T; score: number }[] = [];
  for (const device of devices) {
    const score = scoreField(
      queryNorm,
      queryTokens,
      normalizedNameOf(device),
      weightOf
    );
    if (score > 0) scored.push({ item: device, score });
  }

  return scored
    .sort(
      (a, b) => b.score - a.score || a.item.name.length - b.item.name.length
    )
    .map((r) => r.item);
};

/**
 * Lowercase, strip diacritics, and collapse punctuation so "Eólica" matches "eolica".
 * Keeps all Unicode letters/numbers — the catalog holds Japanese, Cyrillic, etc. names,
 * which an ASCII-only class would erase entirely.
 */
const normalize = (text: string): string =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/**
 * IDF weights over the catalog's name tokens: log(1 + N / (1 + df)). A token in one name
 * out of ten thousand weighs ~9, one in every seventh name ~2; a token the catalog has
 * never seen (a typo, a new device) gets the maximum, since it can only be distinctive.
 */
const tokenWeightsOf = (devices: DeviceSearchText[]): TokenWeight => {
  const cached = tokenWeightCache.get(devices);
  // A membership change on the same array (a push or splice) invalidates the snapshot.
  if (cached && cached.total === devices.length) return cached.weightOf;
  const documentFrequency = new Map<string, number>();
  for (const device of devices) {
    for (const token of new Set(normalizedNameOf(device).split(" "))) {
      documentFrequency.set(token, (documentFrequency.get(token) ?? 0) + 1);
    }
  }
  const total = devices.length;
  const weightOf: TokenWeight = (token) =>
    Math.log(1 + total / (1 + (documentFrequency.get(token) ?? 0)));
  tokenWeightCache.set(devices, { total, weightOf });
  return weightOf;
};

const scoreField = (
  queryNorm: string,
  queryTokens: string[],
  fieldNorm: string,
  weightOf: TokenWeight
): number => {
  if (!fieldNorm) return 0;
  if (fieldNorm === queryNorm) return EXACT_FIELD_SCORE;

  const index = fieldNorm.indexOf(queryNorm);
  if (index !== -1) {
    let score = SUBSTRING_BASE_SCORE;
    if (index === 0) score += 0.5;
    else if (fieldNorm[index - 1] === " ") score += 0.25;
    // Earlier matches read as more relevant; keep the penalty below the bonuses.
    return score - Math.min(index, 100) / 1000;
  }

  return fuzzyTokenScore(queryTokens, fieldNorm.split(" "), weightOf);
};

/**
 * Blend of three signals, each weighted by token rarity (see tokenWeightsOf):
 * - query coverage: how much of what the user typed matches this name — matching the
 *   distinctive word of the query counts far more than matching its generic ones,
 * - field coverage: how much of the name is explained by the query — this lets a device
 *   registered as just "Kaitaia" beat generic-word matches ("Solar", "Farm") when the
 *   user searches a compound name like "Lodestone Energy Kaitaia Solar Farm",
 * - best single-token match, scaled by that token's weight within the query: a perfect hit
 *   on "farm" alone is not a strong hit for "Tullabeg Solar Farm".
 * Listing needs both gates: one strong token hit (unweighted, whatever the word) and a
 * weighted score above MIN_FUZZY_SCORE — a name matching only the generic words of a
 * query with a distinctive word fails the second on purpose (see the module docstring).
 */
const fuzzyTokenScore = (
  queryTokens: string[],
  fieldTokens: string[],
  weightOf: TokenWeight
): number => {
  const fieldBest = new Array<number>(fieldTokens.length).fill(0);
  const queryWeights = queryTokens.map(weightOf);
  // No spread: a pasted wall of text must not blow the call-argument limit.
  const maxQueryWeight = queryWeights.reduce((a, b) => Math.max(a, b), 0);
  let querySum = 0;
  let queryWeightSum = 0;
  let best = 0;
  let weightedBest = 0;
  for (const [i, queryToken] of queryTokens.entries()) {
    let tokenBest = 0;
    for (let j = 0; j < fieldTokens.length; j++) {
      const similarity = tokenSimilarity(queryToken, fieldTokens[j]);
      if (similarity >= MIN_TOKEN_SIMILARITY) {
        fieldBest[j] = Math.max(fieldBest[j], similarity);
      }
      tokenBest = Math.max(tokenBest, similarity);
    }
    if (tokenBest < MIN_TOKEN_SIMILARITY) tokenBest = 0;
    querySum += queryWeights[i] * tokenBest;
    queryWeightSum += queryWeights[i];
    best = Math.max(best, tokenBest);
    weightedBest = Math.max(
      weightedBest,
      (tokenBest * queryWeights[i]) / maxQueryWeight
    );
  }
  if (best < MIN_BEST_TOKEN_SIMILARITY) return 0;
  const queryCoverage = querySum / queryWeightSum;
  let fieldSum = 0;
  let fieldWeightSum = 0;
  for (const [j, fieldToken] of fieldTokens.entries()) {
    const weight = weightOf(fieldToken);
    fieldSum += weight * fieldBest[j];
    fieldWeightSum += weight;
  }
  const fieldCoverage = fieldSum / fieldWeightSum;
  const score =
    0.45 * queryCoverage + 0.25 * fieldCoverage + 0.3 * weightedBest;
  return score >= MIN_FUZZY_SCORE ? score : 0;
};

const tokenSimilarity = (queryToken: string, fieldToken: string): number => {
  // Single characters carry no signal (punctuation fragments like "d.o.o." -> "d o o");
  // one-character queries still work via the exact-substring path in scoreField.
  if (queryToken.length < 2 || fieldToken.length < 2) return 0;
  if (queryToken === fieldToken) return 1;
  // Prefix matches cover search-as-you-type ("kaita" -> "kaitaia"). The matched prefix
  // must be a few characters long — normalization can leave one-letter fragments (e.g.
  // "d.o.o." -> "d o o"), and those must not count as strong matches for any query.
  if (queryToken.length >= 2 && fieldToken.startsWith(queryToken)) return 0.92;
  if (fieldToken.length >= 3 && queryToken.startsWith(fieldToken)) return 0.92;
  if (queryToken.length >= 3 && fieldToken.includes(queryToken)) return 0.85;
  // Levenshtein for typo tolerance; too short or too different isn't worth computing.
  const maxLength = Math.max(queryToken.length, fieldToken.length);
  if (queryToken.length < 4 || fieldToken.length < 4) return 0;
  if (Math.abs(queryToken.length - fieldToken.length) > maxLength / 2) return 0;
  return 1 - levenshtein(queryToken, fieldToken) / maxLength;
};

const levenshtein = (a: string, b: string): number => {
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  let current = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitutionCost
      );
    }
    [previous, current] = [current, previous];
  }
  return previous[b.length];
};
